export const modulesPart2 = [
  {
    id: 6,
    slug: "ad-creatives-copywriting",
    title: "Ad Creatives & Copywriting",
    color: "pink",
    tagline: "Creative hi naya targeting hai. Hook aisa jo scroll roke.",
    summary: "Hooks, AIDA & PAS, formats, UGC, and creative fatigue.",
    sections: [
      {
        id: "hook",
        h: "The first 3 seconds",
        p: ["People scroll fast. If the ==first 3 seconds== don't stop the thumb, nothing else in your ad matters."],
        sketch: "hookTimeline",
        example: {
          title: "Hook examples",
          lines: ["'Ye T-shirt 50 wash ke baad bhi aisi dikhti hai…' (show the proof)", "'Stop buying ₹2,000 shoes for running.' (pattern break)", "'Mumbai mein 2BHK ₹65L se — yes, really.' (number + location)"],
        },
      },
      {
        id: "frameworks",
        h: "Copy frameworks",
        steps: [
          { t: "AIDA", d: "Attention → Interest → Desire → Action. Classic, works for most ads." },
          { t: "PAS", d: "Problem → Agitate → Solution. 'Hair fall? Har baar comb pe baal? Try…'" },
          { t: "Before–After–Bridge", d: "Show life before, life after, and your product as the bridge." },
        ],
      },
      {
        id: "formats",
        h: "Formats",
        table: {
          head: ["Format", "Good for"],
          rows: [
            ["Static image", "Clear offer, quick testing"],
            ["Carousel", "Multiple products or step-by-step story"],
            ["Reel / video", "Demo, emotion, storytelling"],
            ["UGC", "Trust — real people using the product"],
          ],
        },
      },
      {
        id: "measure",
        h: "How to judge a creative",
        points: ["==Hook rate / thumb-stop== = 3-second video plays ÷ impressions.", "==CTR== — is the message making people click?", "==CPA / ROAS== — the final verdict.", "Good CTR + bad CVR → problem is the landing page, not the ad."],
      },
      {
        id: "fatigue",
        h: "Creative fatigue",
        p: ["Same ad, same audience, again and again → ==frequency rises, CTR falls, CPA climbs==."],
        hinglish: "Har 2–3 hafte naye angles test karo. Ek winning ad hamesha winner nahi rehta.",
      },
    ],
    takeaways: ["Hook decides everything in 3 seconds.", "Use AIDA or PAS to structure copy.", "Watch frequency and CTR for fatigue."],
    quiz: [
      { q: "PAS stands for…", options: ["Price, Ads, Sales", "Problem, Agitate, Solution", "Pixel, Audience, Spend"], answer: 1, why: "Problem → Agitate → Solution." },
      { q: "High CTR but low conversions usually means…", options: ["Landing page problem", "Hook problem", "Pixel is perfect"], answer: 0, why: "The ad works; the page doesn't." },
      { q: "Sign of creative fatigue?", options: ["CTR rising", "Frequency up, CTR down", "CPM down"], answer: 1, why: "Same people seeing it too often." },
    ],
  },
  {
    id: 7,
    slug: "landing-pages-cro",
    title: "Landing Pages & CRO",
    color: "yellow",
    tagline: "Ad pe click karwaya, page boring nikla? Conversion gaya.",
    summary: "Message match, above the fold, trust, speed, forms and A/B testing.",
    sections: [
      {
        id: "message-match",
        h: "Message match",
        p: ["The page must ==continue the promise of the ad==. Ad says '50% off sneakers' → page opens on those sneakers with 50% off. Not the homepage."],
        sketch: "wireframe",
        hinglish: "Google Ads mein keyword ko headline aur page copy mein rakho — ad relevance aur Quality Score dono badhte hain.",
      },
      {
        id: "anatomy",
        h: "Anatomy of a converting page",
        steps: [
          { t: "Above the fold", d: "Headline with the benefit, one image, one CTA." },
          { t: "Proof", d: "Reviews, ratings, client logos, before–after." },
          { t: "Details", d: "Features as benefits, pricing, FAQs that kill objections." },
          { t: "Risk reversal", d: "Guarantee, easy returns, COD, secure payment badges." },
          { t: "Repeat CTA", d: "Sticky button on mobile." },
        ],
      },
      {
        id: "speed",
        h: "Speed and mobile",
        points: ["Most ad traffic is on mobile — ==design for the phone first==.", "Aim for the page to load in under ~3 seconds; compress images.", "Fewer form fields = more leads. Ask only what sales really needs."],
      },
      {
        id: "cro",
        h: "CRO — Conversion Rate Optimisation",
        p: ["CVR = conversions ÷ visitors × 100. Moving CVR from ==2% to 3% is 50% more sales== at the same ad spend."],
        example: {
          title: "A/B test ideas",
          lines: ["Headline: feature vs benefit.", "CTA: 'Buy now' vs 'Get mine at 40% off'.", "Form: 6 fields vs 3 fields.", "Change one thing at a time and wait for enough data."],
        },
      },
    ],
    takeaways: ["Ad promise = page headline.", "Mobile-first and fast.", "Test one change at a time."],
    quiz: [
      { q: "Ad promises '50% off sneakers'. Send traffic to…", options: ["Homepage", "Sneaker page showing 50% off", "About us"], answer: 1, why: "Message match." },
      { q: "CVR goes 2% → 4% at same spend. Sales…", options: ["Same", "Double", "Half"], answer: 1, why: "Twice the conversion rate = twice the sales." },
      { q: "Best way to get more form leads?", options: ["Add more fields", "Remove unnecessary fields", "Hide the form"], answer: 1, why: "Less friction, more submissions." },
    ],
  },
  {
    id: 8,
    slug: "retargeting-remarketing",
    title: "Retargeting & Remarketing",
    color: "green",
    tagline: "Jo pehli baar mein nahi khareeda, use yaad dilao.",
    summary: "Warm audiences, cart abandoners, upsell, exclusions and frequency.",
    sections: [
      {
        id: "difference",
        h: "Retargeting vs Remarketing",
        table: {
          head: ["", "Retargeting", "Remarketing"],
          rows: [
            ["Who", "People who visited or engaged but didn't buy", "Existing customers / past contacts"],
            ["How", "Ads using Pixel / engagement audiences", "Email, WhatsApp, SMS, customer-list ads"],
            ["Goal", "Bring them back to complete the action", "Repeat purchase, upsell, cross-sell"],
          ],
        },
        p: ["In practice people use both words loosely. What matters is ==who you are talking to and what you want next==."],
      },
      {
        id: "loop",
        h: "The retargeting loop",
        sketch: "retargetLoop",
        steps: [
          { t: "Viewed product, 7 days", d: "Show the same product + reviews (Dynamic / catalog ads)." },
          { t: "Added to cart, 3–7 days", d: "Reminder + small push: free shipping or limited stock." },
          { t: "Engaged on Instagram, 30 days", d: "Introduce the brand story and best-sellers." },
        ],
      },
      {
        id: "upsell",
        h: "Upsell existing customers",
        example: {
          title: "Skincare brand",
          lines: ["Bought face wash → after 20 days show moisturiser (cross-sell).", "Bought 1 bottle → offer 3-pack combo (upsell).", "Bottle lasts 45 days → WhatsApp reminder on day 40 (repeat)."],
        },
        hinglish: "Naya customer laana mehenga hai, purane ko dobara bechna sasta. LTV yahin se badhta hai.",
      },
      {
        id: "rules",
        h: "Rules that save budget",
        points: ["==Exclude purchasers== from 'buy now' retargeting.", "Watch frequency — showing the same ad 15 times annoys people.", "Keep retargeting budget a smaller share; it only works if fresh traffic keeps coming in."],
      },
    ],
    takeaways: ["Segment by how warm the person is.", "Upsell and repeat drive LTV.", "Always exclude recent buyers."],
    quiz: [
      { q: "Someone added to cart but left. Best ad?", options: ["Brand awareness video", "Cart reminder with free shipping", "Job opening post"], answer: 1, why: "They're at the bottom of the funnel — give a push." },
      { q: "Why exclude purchasers?", options: ["To waste budget", "So you don't pay to sell to people who just bought", "Meta requires it"], answer: 1, why: "Avoid wasted spend and annoyance." },
      { q: "Selling a moisturiser to a face-wash buyer is…", options: ["Cross-sell", "Cold targeting", "SEO"], answer: 0, why: "Related product to an existing customer." },
    ],
  },
  {
    id: 9,
    slug: "optimization-scaling",
    title: "Campaign Optimization & Scaling",
    color: "blue",
    tagline: "Campaign profitable hai? Ab smartly scale karo.",
    summary: "ABO vs CBO, vertical vs horizontal scaling, kill rules and diagnosis.",
    sections: [
      {
        id: "abo-cbo",
        h: "ABO vs CBO",
        sketch: "aboCbo",
        table: {
          head: ["", "ABO (Ad set budget)", "CBO (Advantage campaign budget)"],
          rows: [
            ["Budget set at", "Each ad set", "Campaign level"],
            ["Control", "You decide spend per audience", "Meta shifts money to best ad sets"],
            ["Best for", "Testing audiences / creatives fairly", "Scaling proven winners"],
          ],
        },
        hinglish: "Test ABO mein, scale CBO mein — ye ek common aur kaam ka approach hai.",
      },
      {
        id: "scaling",
        h: "Two ways to scale",
        steps: [
          { t: "Vertical scaling", d: "Increase budget on a winner gradually — around 20% every few days — so performance doesn't crash." },
          { t: "Horizontal scaling", d: "Duplicate winners into new audiences, new creatives, new placements or new geos." },
        ],
        sketch: "scaleGraph",
      },
      {
        id: "diagnose",
        h: "Diagnose before you touch",
        table: {
          head: ["Symptom", "Likely cause", "Fix"],
          rows: [
            ["High CPM", "Narrow audience / competitive season", "Broaden audience, refresh creative"],
            ["Low CTR", "Weak hook or offer", "New hooks, new angle"],
            ["Good CTR, low CVR", "Landing page / price / trust", "CRO, reviews, offer"],
            ["CPA rising slowly", "Creative fatigue", "Rotate in new creatives"],
          ],
        },
      },
      {
        id: "kill",
        h: "Simple kill & scale rules",
        points: ["Spent ~2–3× target CPA with zero conversions → pause.", "CPA stable within target for 3–5 days → scale.", "Don't judge an ad set on one day's data.", "Make one big change at a time."],
      },
    ],
    takeaways: ["ABO to test, CBO to scale.", "Scale slowly vertically, widely horizontally.", "Diagnose with CPM → CTR → CVR."],
    quiz: [
      { q: "Budget is set per ad set in…", options: ["ABO", "CBO", "SEO"], answer: 0, why: "Ad set Budget Optimisation." },
      { q: "Adding new audiences with a winning ad is…", options: ["Vertical scaling", "Horizontal scaling", "Retargeting"], answer: 1, why: "Expanding sideways." },
      { q: "Good CTR, poor conversions — look at…", options: ["Landing page", "Hook", "Logo colour"], answer: 0, why: "Clicks are coming, the page isn't converting." },
    ],
  },
  {
    id: 10,
    slug: "industry-strategies",
    title: "Industry-Specific Strategies",
    color: "pink",
    tagline: "E-commerce, real estate, local, coaching — sabka alag game.",
    summary: "TOFU / MOFU / BOFU funnels and playbooks per industry.",
    sections: [
      {
        id: "funnel",
        h: "TOFU · MOFU · BOFU",
        sketch: "funnel",
        table: {
          head: ["Stage", "Who", "Content", "Metric"],
          rows: [
            ["TOFU", "Cold — never heard of you", "Reels, problem awareness, broad reach", "CPM, hook rate"],
            ["MOFU", "Warm — engaged/visited", "Reviews, demos, comparisons", "CTR, CPL"],
            ["BOFU", "Hot — cart / lead / repeat", "Offers, urgency, guarantees", "CPA, ROAS"],
          ],
        },
      },
      {
        id: "playbooks",
        h: "Industry playbooks",
        playbooks: [
          { name: "D2C E-commerce", color: "yellow", lines: ["Sales objective + catalog ads", "Track Purchase with value", "Watch RTO by pincode, push prepaid", "Retarget cart abandoners"] },
          { name: "Real estate", color: "blue", lines: ["Leads objective, higher-intent form", "Qualify: budget, location, timeline", "Call within minutes", "Use sales remarks to fix targeting"] },
          { name: "Local cafe / clinic", color: "green", lines: ["4–5 km radius", "Offer-led creative", "Calls, directions, WhatsApp", "Google Business Profile + Search"] },
          { name: "Coaching / EdTech", color: "pink", lines: ["Free webinar or demo class", "Reminders on WhatsApp", "Retarget attendees with offer", "Track cost per enrolment"] },
          { name: "B2B / SaaS", color: "purple", lines: ["Google Search for intent", "LinkedIn for job titles", "Lead magnet → demo call", "Long cycle: measure CAC vs LTV"] },
        ],
      },
      {
        id: "choose",
        h: "Which funnel when?",
        points: ["==Low price, impulse buy== (₹299 tee) → short funnel, mostly BOFU-style direct sale.", "==High price, thought-through== (₹70L flat) → longer funnel, nurture with MOFU content.", "New brand → spend more on TOFU; established brand → more on BOFU + retention."],
        hinglish: "Funnel product ki price aur customer ke sochne ke time se decide hota hai.",
      },
    ],
    takeaways: ["Match content to funnel stage.", "Each industry has a different conversion event.", "Price & decision time decide funnel length."],
    quiz: [
      { q: "Reviews and demo videos fit best in…", options: ["TOFU", "MOFU", "BOFU"], answer: 1, why: "Warm people evaluating options." },
      { q: "Real estate lead quality issue — first step?", options: ["Increase budget", "Get sales remarks & add qualifying questions", "Stop tracking"], answer: 1, why: "Fix the input with real feedback." },
      { q: "₹70 lakh flat needs…", options: ["Short impulse funnel", "Longer nurture funnel", "No funnel"], answer: 1, why: "High price = longer decision time." },
    ],
  },
];
